import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ScrollText, RefreshCw, Loader2, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination, type PageMeta } from '@/components/Pagination';
import { apiCall } from '@/lib/api';

interface AuditRow {
  id: string;
  organization_id: string | null;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  before_json: string | null;
  after_json: string | null;
  context_json: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

export default function AuditLog() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<AuditRow[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const query: Record<string, string | number> = { page, per_page: 50 };
      if (actionFilter) query.action = actionFilter;
      const r = await apiCall<{ items: AuditRow[] } & PageMeta>('/v1/audit-log', { bearerToken: token, query });
      setItems(r.items);
      setPageMeta({ page: r.page, per_page: r.per_page, total: r.total, total_pages: r.total_pages });
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken, page, actionFilter]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [actionFilter]);

  const actionColor = (action: string): 'destructive' | 'default' | 'outline' => {
    if (action.endsWith('.delete') || action.endsWith('.cancel') || action.endsWith('.disconnect')) return 'destructive';
    if (action.endsWith('.create') || action.endsWith('.connect')) return 'default';
    return 'outline';
  };

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><ScrollText className="h-6 w-6" /> Audit log</h1>
          <p className="text-sm text-muted-foreground">Every admin action with actor, target, and before/after state. Append-only.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            placeholder="action prefix (e.g. member.)"
            className="text-sm border rounded px-3 py-1.5 w-56"
          />
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </div>

      {err && <Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-3 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {err}</CardContent></Card>}

      {!loading && items.length === 0 && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">No audit entries.</CardContent></Card>
      )}

      <Card>
        <CardContent className="p-0">
          {items.map((row) => {
            const isOpen = expanded === row.id;
            return (
              <div key={row.id} className="border-b last:border-0">
                <button
                  className="w-full px-4 py-2 flex items-center gap-3 hover:bg-muted/40 text-left text-sm"
                  onClick={() => setExpanded(isOpen ? null : row.id)}
                >
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                  <span className="text-xs text-muted-foreground tabular-nums w-32 shrink-0">{new Date(row.created_at.replace(' ', 'T') + 'Z').toLocaleString()}</span>
                  <Badge variant={actionColor(row.action)} className="font-mono text-xs">{row.action}</Badge>
                  <span className="text-xs text-muted-foreground truncate">{row.actor_email ?? 'system'}</span>
                  <span className="text-xs text-muted-foreground font-mono truncate ml-auto">{row.resource_type}/{row.resource_id?.slice(0, 8) ?? '—'}</span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-3 pl-11 text-xs space-y-2 bg-muted/20">
                    {row.before_json && (
                      <div><span className="text-muted-foreground font-medium">before:</span>
                        <pre className="mt-1 p-2 bg-background border rounded overflow-x-auto">{prettyJson(row.before_json)}</pre>
                      </div>
                    )}
                    {row.after_json && (
                      <div><span className="text-muted-foreground font-medium">after:</span>
                        <pre className="mt-1 p-2 bg-background border rounded overflow-x-auto">{prettyJson(row.after_json)}</pre>
                      </div>
                    )}
                    {row.context_json && (
                      <div><span className="text-muted-foreground font-medium">context:</span>
                        <pre className="mt-1 p-2 bg-background border rounded overflow-x-auto">{prettyJson(row.context_json)}</pre>
                      </div>
                    )}
                    <div className="text-muted-foreground/70 pt-1">
                      {row.ip && <>ip: <span className="font-mono">{row.ip}</span> · </>}
                      {row.user_agent && <>ua: <span className="font-mono truncate inline-block max-w-md align-bottom">{row.user_agent}</span></>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Pagination meta={pageMeta} onPageChange={setPage} itemLabel="entries" />
    </div>
  );
}

function prettyJson(raw: string): string {
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}
